import { ERROR_REPORT_LIMITS, type ErrorReport } from '../../shared/errorReport';

const ENDPOINT = '/api/errors';
export const MAX_REPORTS_PER_SESSION = 20;

type Kind = ErrorReport['kind'];

export interface ReporterDeps {
  send(report: ErrorReport): void;
  build: string;
  /** The current `location.pathname`. */
  pathname(): string;
  userAgent: string;
  online(): boolean;
}

// Reports carry no item content, so anything that looks like an address, a token or a query
// string is removed from text the app did not write itself.
const SCRUBBERS: [RegExp, string][] = [
  [/[^\s@<>()"',;:]+@[^\s@<>()"',;:]+\.[a-z]{2,}/gi, '[email]'],
  [/eyJ[\w-]+\.[\w-]+\.[\w-]*/g, '[token]'],
  [/\b(Bearer|Basic)\s+\S+/gi, '$1 [token]'],
  [/(https?:\/\/[^\s?#)]+)[?#][^\s)]*/g, '$1'],
  [/\/invite\/[^\s/?#)]+/g, '/invite/[token]'],
  [/[A-Za-z0-9_-]{32,}/g, '[token]'],
];

export function scrub(text: string): string {
  return SCRUBBERS.reduce((t, [re, to]) => t.replace(re, to), text);
}

/** The first path segment only: deeper segments are ids or invite tokens. */
export function coarseRoute(pathname: string): string {
  const [first, ...rest] = pathname.split('/').filter(Boolean);
  if (!first) return '/';
  return `/${first}${rest.length ? '/:param' : ''}`.slice(0, ERROR_REPORT_LIMITS.route);
}

/** Browser family and major version plus OS family, e.g. `Chrome 141; Android`. */
export function coarseUserAgent(ua: string): string {
  const browser =
    match(ua, /SamsungBrowser\/(\d+)/, 'Samsung') ??
    match(ua, /Edg\/(\d+)/, 'Edge') ??
    match(ua, /Firefox\/(\d+)/, 'Firefox') ??
    match(ua, /CriOS\/(\d+)/, 'Chrome') ??
    match(ua, /Chrome\/(\d+)/, 'Chrome') ??
    match(ua, /Version\/(\d+)[\d.]* .*Safari\//, 'Safari') ??
    'other';
  const os = /Android/.test(ua)
    ? 'Android'
    : /iPhone|iPad|iPod/.test(ua)
      ? 'iOS'
      : /Windows/.test(ua)
        ? 'Windows'
        : /CrOS/.test(ua)
          ? 'ChromeOS'
          : /Mac OS X|Macintosh/.test(ua)
            ? 'macOS'
            : /Linux/.test(ua)
              ? 'Linux'
              : 'other';
  return `${browser}; ${os}`.slice(0, ERROR_REPORT_LIMITS.ua);
}

function match(ua: string, re: RegExp, name: string): string | undefined {
  const m = re.exec(ua);
  return m ? `${name} ${m[1]}` : undefined;
}

// Objects are never serialized: an item or a session passed to console.error stays on the device.
function describe(value: unknown): { text: string; stack?: string } {
  if (value instanceof Error) {
    return { text: `${value.name}: ${value.message}`, stack: value.stack };
  }
  if (typeof value === 'string') return { text: value };
  if (typeof value === 'number' || typeof value === 'boolean') return { text: String(value) };
  if (value === null || value === undefined) return { text: String(value) };
  return { text: '[object]' };
}

export function createErrorReporter(deps: ReporterDeps) {
  let sent = 0;
  const seen = new Set<string>();

  return function report(kind: Kind, values: unknown[]): void {
    if (sent >= MAX_REPORTS_PER_SESSION || !deps.online()) return;
    const parts = values.map(describe);
    const message = scrub(parts.map((p) => p.text).join(' ')).slice(0, ERROR_REPORT_LIMITS.message);
    const rawStack = parts.find((p) => p.stack)?.stack;
    const stack = rawStack ? scrub(rawStack).slice(0, ERROR_REPORT_LIMITS.stack) : undefined;
    const key = `${kind}\n${message}\n${stack ?? ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    sent++;
    deps.send({
      kind,
      message,
      stack,
      build: deps.build.slice(0, ERROR_REPORT_LIMITS.build),
      route: coarseRoute(deps.pathname()),
      ua: coarseUserAgent(deps.userAgent),
    });
  };
}

function sendReport(report: ErrorReport): void {
  void fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(report),
    credentials: 'same-origin',
    keepalive: true,
  }).catch(() => {});
}

/**
 * Sends uncaught errors, unhandled rejections and console.error calls to the backend.
 * Returns a function that removes the listeners and restores console.error.
 */
export function installErrorReporting(
  win: Window = window,
  deps: Partial<ReporterDeps> = {},
): () => void {
  const report = createErrorReporter({
    send: sendReport,
    build: import.meta.env.VITE_BUILD_ID ?? 'dev',
    pathname: () => win.location.pathname,
    userAgent: win.navigator.userAgent,
    online: () => win.navigator.onLine !== false,
    ...deps,
  });
  // A failure inside report, or a console.error it triggers, must not report itself.
  let reporting = false;
  const guarded = (kind: Kind, values: unknown[]) => {
    if (reporting) return;
    reporting = true;
    try {
      report(kind, values);
    } catch {
      // Reporting is best effort.
    } finally {
      reporting = false;
    }
  };

  const onError = (event: ErrorEvent) => guarded('error', [event.error ?? event.message]);
  const onRejection = (event: PromiseRejectionEvent) =>
    guarded('unhandledrejection', [event.reason]);
  win.addEventListener('error', onError);
  win.addEventListener('unhandledrejection', onRejection);

  const originalError = console.error;
  console.error = (...args: unknown[]) => {
    originalError.apply(console, args);
    guarded('console', args);
  };

  return () => {
    win.removeEventListener('error', onError);
    win.removeEventListener('unhandledrejection', onRejection);
    console.error = originalError;
  };
}
